import React, { useContext } from 'react';

import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import InfoIcon from '@mui/icons-material/Info';

import { PTWContext } from '../sections/context/PTWContext';
import { get, set } from 'lodash';

const PTWTable = ({ rows, onChange, name = 'PTWTable', categories = [], values, disabled }) => {
	const { isPrinting } = useContext(PTWContext);

	const tableValues = get(values, name) || [];

	const handleChange = (rowIndex, key, value) => {
		const prev = tableValues[rowIndex] || {};
		tableValues[rowIndex] = set(prev, key, value);
		onChange({ target: { name, value: tableValues } });
	};

	const categoriesVisible = categories.filter((c) => isPrinting || !c.printOnly);
	const columnsVisible = categoriesVisible.reduce((p, c) => p + c.columns?.length || 0, [0]) + 2;

	const getCellContent = (cat, c, rowValue, rowIndex) => {
		if (cat.isRadio)
			return (
				<Radio
					checked={rowValue[cat.name] === c.value}
					onChange={() => handleChange(rowIndex, cat.name, c.value)}
					disabled={disabled}
				/>
			);
		if (c.component === 'TextField')
			return (
				<TextField
					fullWidth
					value={rowValue[cat.name]?.[c.name]}
					onChange={({ target: { value } }) => handleChange(rowIndex, `${cat.name}.${c.name}`, value)}
					disabled={disabled}
				/>
			);
		return (
			<Checkbox
				checked={!!rowValue[cat.name]}
				onChange={({ target: { checked } }) => handleChange(rowIndex, cat.name, checked)}
				disabled={disabled}
			/>
		);
	};

	return (
		<TableContainer>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell colSpan={2}>&nbsp;</TableCell>
						{categoriesVisible.map((c, i) => (
							<TableCell key={i} colSpan={c.columns?.length} align="center">
								{c.label}
							</TableCell>
						))}
					</TableRow>
					<TableRow>
						<TableCell colSpan={2}>&nbsp;</TableCell>
						{categoriesVisible.flatMap((cat, ci) =>
							cat.columns?.map((c, i) => (
								<TableCell key={`${ci}-${i}`} align="center" width={c.width || 100}>
									{c.label}
								</TableCell>
							)),
						)}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row, rowIndex) => {
						const rowValue = tableValues[rowIndex] || {};

						const cells = categoriesVisible.flatMap((cat, ci) => {
							return cat.columns?.map((c, i) => (
								<TableCell key={`${ci}-${i}`} align="center">
									{getCellContent(cat, c, rowValue, rowIndex)}
								</TableCell>
							));
						});

						const info = row.info && (
							<Tooltip title={row.info}>
								<InfoIcon
									sx={{ position: 'absolute', paddingBottom: 1, opacity: 0.8, '&:hover': { cursor: 'pointer' } }}
								/>
							</Tooltip>
						);

						if (row.header)
							return (
								<TableRow key={rowIndex}>
									<TableCell sx={{ lineHeight: '20px', backgroundColor: '#CCC' }} colSpan={columnsVisible}>
										{row.header}
									</TableCell>
								</TableRow>
							);

						return (
							<TableRow key={rowIndex}>
								<TableCell sx={{ lineHeight: '20px' }} colSpan={row.subLabel ? 1 : 2}>
									{row.label} {!row.subLabel && info}
								</TableCell>
								{row.subLabel && (
									<TableCell sx={{ lineHeight: '20px' }}>
										{row.subLabel} {info}
									</TableCell>
								)}

								{row.noChecks ? <TableCell colspan={columnsVisible - 2}></TableCell> : cells}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

PTWTable.type = 'PTWTable';

PTWTable.spec = [
	{
		name: 'PTWTable',
		key: 'PTWTable',
		title: 'PTW table',
		icon: 'Circle',
		category: 'input',
		translatable: [
			'label',
			'rows.label',
			'rows.subLabel',
			'rows.header',
			'rows.info',
			'categories.label',
			'categories.columns.label',
		],
		fields: [
			{
				type: 'TextField',
				label: 'Label',
				name: 'label',
			},
			{
				type: 'Checkbox',
				label: 'Required?',
				name: 'required',
			},
			{
				type: 'repeater',
				label: 'Categories',
				name: 'categories',
				fields: [
					{
						type: 'TextField',
						name: 'label',
						label: 'Category label',
					},
					{
						type: 'TextField',
						name: 'name',
						label: 'Category name (not translated)',
					},
					{
						type: 'repeater',
						label: 'Columns',
						name: 'columns',
						fields: [
							{
								type: 'TextField',
								name: 'label',
								label: 'Column label',
							},
							{
								type: 'TextField',
								name: 'value',
								label: 'Column value (not translated)',
							},
						],
					},
				],
			},
			{
				type: 'repeater',
				label: 'Rows',
				name: 'rows',
				fields: [
					{
						type: 'TextField',
						name: 'label',
						label: 'Row label',
					},
					{
						type: 'TextField',
						name: 'subLabel',
						label: 'Row sub-label',
					},
					{
						type: 'TextField',
						name: 'header',
						label: 'Section header',
					},
					{
						type: 'TextArea',
						name: 'info',
						label: 'Info tooltip',
					},
				],
			},
			{
				type: 'repeater',
				label: 'Radio Options',
				name: 'options',
				fields: [
					{
						type: 'TextField',
						name: 'label',
						label: 'option label',
					},
					{
						type: 'TextField',
						name: 'value',
						label: 'option value',
					},
				],
			},
			{
				type: 'TextField',
				label: 'Print Label',
				name: 'printLabel',
			},
			{
				type: 'repeater',
				label: 'Print Radio Options',
				name: 'printOptions',
				fields: [
					{
						type: 'TextField',
						name: 'label',
						label: 'option label',
					},
					{
						type: 'TextField',
						name: 'value',
						label: 'option value',
					},
				],
			},
		],
	},
];

export default PTWTable;
